import {useCallback, useEffect, useMemo} from "react";
import {DateRangePill} from "~/components/dateRangePill";
import {
    applyCreatedAtDateRangeToFilters,
    getDefaultLast30DaysDateRange,
    readCreatedAtDateRangeFromFilters,
    shouldApplyDefaultCreatedAtMonthFilter,
} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import {getDataViewerDateRangeColumnId} from "~/components/ottopilotDataViewer/mobileFilterDrawerHelpers";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export {getDataViewerDateRangeColumnId} from "~/components/ottopilotDataViewer/mobileFilterDrawerHelpers";

export function DataViewerDateRangeFilter<T extends {id: Uuid}>({
    controller,
}: {
    controller: OttopilotDataViewerController<T>;
}) {
    const columnId = getDataViewerDateRangeColumnId(controller);
    const applyDefaultCreatedAtMonthFilter = shouldApplyDefaultCreatedAtMonthFilter(
        controller.defaultCreatedAtMonthFilter,
    );
    const defaultRange = useMemo(() => getDefaultLast30DaysDateRange(), []);

    const rawValue = useMemo(
        (): [Iso8601DateTime | null, Iso8601DateTime | null] =>
            readCreatedAtDateRangeFromFilters(controller.filterConfiguration.lineItems, columnId),
        [controller.filterConfiguration.lineItems, columnId],
    );

    const value = useMemo((): [Iso8601DateTime | null, Iso8601DateTime | null] => {
        if (rawValue[0] !== null && rawValue[1] !== null) {
            return [rawValue[0], rawValue[1]];
        }
        if (applyDefaultCreatedAtMonthFilter === true) {
            return defaultRange;
        }
        return [null, null];
    }, [applyDefaultCreatedAtMonthFilter, rawValue, defaultRange]);

    useEffect(() => {
        if (applyDefaultCreatedAtMonthFilter === false) {
            return;
        }
        if (rawValue[0] !== null && rawValue[1] !== null) {
            return;
        }
        controller.setFilterConfiguration((prev) =>
            applyCreatedAtDateRangeToFilters(prev, defaultRange, columnId),
        );
    }, [
        applyDefaultCreatedAtMonthFilter,
        columnId,
        controller.filterConfiguration.revisionId,
        controller.setFilterConfiguration,
        defaultRange,
        rawValue,
    ]);

    const onChange = useCallback(
        (range: [Iso8601DateTime | null, Iso8601DateTime | null]) => {
            controller.setFilterConfiguration((prev) => applyCreatedAtDateRangeToFilters(prev, range, columnId));
        },
        [controller, columnId],
    );

    return (
        <div className="relative inline-block shrink-0">
            <DateRangePill
                value={value}
                onChange={onChange}
                matchFilterPills={true}
                shouldRenderToParent={false}
                maxDate={new Date()}
                showQuickPresets={true}
            />
        </div>
    );
}
